import printJS from "print-js";
import React, { useEffect, useState } from "react";
import { Document, Page } from "react-pdf/dist/esm/entry.webpack";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { Box, Flex } from "rebass";
import { ToastMessage } from "../components/order/ToastMessage";
import Download from "../icons/Download";
import Print from "../icons/Print";
import { MainLayout } from "../layouts/MainLayout";
import { yodaCustomerActions, yodaOrdersActions } from "../logic/actions";
import { LoadingFrame } from "../components/common/LoadingFrame";
import OrderData from "../components/order/OrderData";
import useResponsive from "../hooks/useResponsive";
import Tooltip from "../components/common/Tooltip";
import { getLocalizedString } from "../utils";
import useGuardCustomerRoute from "../hooks/useGuardCustomerRoute";
import {
  Redirect,
  useHistory,
} from "react-router-dom/cjs/react-router-dom.min";
import ConfigurationCheck from "../components/order/ConfigurationCheck";
import { ReactComponent as LeftArrowIcon } from "../static/images/arrow_back.svg";
import CannotOrderMessage from "../components/order/CannotOrderMessage";

const OrderReportPage = () => {
  const dispatch = useDispatch();
  const { orderId } = useParams();

  const [showInfo, setShowInfo] = useState(false);

  const { customer } = useSelector((state) => state.yodaCustomer);
  const { report } = useSelector((state) => state.yodaOrders);
  const { order, error } = useSelector((state) => state.yodaOrders);
  const { me } = useSelector((state) => state.yodaStakeholders);

  useGuardCustomerRoute({ matchOrder: true });

  const history = useHistory();
  const { isMobile } = useResponsive();

  const [base64Report, setBase64Report] = useState();

  useEffect(() => {
    return () => {
      dispatch(yodaOrdersActions.resetOldOrder());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (orderId) {
      dispatch(yodaOrdersActions.getOrderById(orderId));
      dispatch(
        yodaOrdersActions.getOrderConfiguration(
          localStorage.getItem("selectedCustomer"),
        ),
      );

      dispatch(
        yodaCustomerActions.getOne(localStorage.getItem("selectedCustomer")),
      );
      dispatch(
        yodaOrdersActions.generateReport(
          orderId,
          localStorage.getItem("B2BLanguage") == "ITA" ? "IT" : "EN",
        ),
      );
    }
  }, [orderId]);

  const [numPages, setNumPages] = useState(null);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
  }

  async function blobToBase64Async(blob) {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.onerror = (e) => reject(fileReader.error);
      fileReader.onloadend = (e) => {
        const dataUrl = fileReader.result;
        const base64 = dataUrl.substring(dataUrl.indexOf(",") + 1);
        resolve(base64);
      };
      fileReader.readAsDataURL(blob);
    });
  }

  useEffect(() => {
    async function exec() {
      if (report) {
        setBase64Report(await blobToBase64Async(report));
      }
    }

    exec();
  }, [report]);

  if (me?.error === "not-authorized") {
    return (
      <Redirect
        to={{
          pathname: "/not-authorized",
          state: {
            yodaError: true,
          },
        }}
      />
    );
  }
  return (
    <MainLayout>
      <LoadingFrame order={true}></LoadingFrame>
      <ToastMessage
        message={error && error.message}
        confirmFunction={() => {
          dispatch(yodaOrdersActions.resetError());
        }}
        style={{ width: 600 }}
      />
      <ConfigurationCheck
        message={<CannotOrderMessage />}
        buttonLabelCode="ORDER_PAGE_BACK_BUTTON"
        confirmFunction={() => {
          history.push("/orders/" + order?.stakeholderId);
        }}
      />
      <OrderData
        actions={
          order?.status === "Draft" ||
          order?.status === "WaitingForConfirmation" ? (
            <Link
              to={"/orderDetail/" + order?.id}
              onClick={(e) => {
                e.stopPropagation();
              }}
              sx={{ pointer: "cursor" }}
            >
              <Tooltip content={getLocalizedString("BACK_TO_CART")}>
                <Box>
                  <LeftArrowIcon width={26} height={26} />
                </Box>
              </Tooltip>
            </Link>
          ) : null
        }
        {...{ order, customer, showInfo, setShowInfo }}
      />
      <Flex
        backgroundColor={"#A2A2A2"}
        height={40}
        alignItems="center"
        justifyContent={"flex-end"}
        pr={3}
        m="10px"
      >
        <Box sx={{ mr: ".5rem" }}>
          {report && base64Report && (
            <Tooltip content={getLocalizedString("DOWNLOAD")}>
              <a
                download={order?.orderNumber + ".pdf"}
                href={`data:application/pdf;base64,${base64Report}`}
                title={order?.orderNumber + ".pdf"}
              >
                <Download
                  style={{
                    color: "#E3E3E3",
                  }}
                />
              </a>
            </Tooltip>
          )}
        </Box>
        {report && base64Report && (
          <Tooltip content={getLocalizedString("PRINT")}>
            <Box
              style={{ cursor: "pointer" }}
              onClick={() => {
                printJS({ printable: base64Report, type: "pdf", base64: true });
              }}
            >
              <Print
                style={{
                  color: "#E3E3E3",
                  width: 24,
                  cursor: "pointer",
                }}
              />
            </Box>
          </Tooltip>
        )}
      </Flex>
      {report && base64Report && (
        <Flex
          width={1}
          height={"100%"}
          overflow={"scroll"}
          backgroundColor={["#f2f2f2", "#F2F2F2", "#F2F2F2"]}
          p={[null, 4, 4]}
          justifyContent={"center"}
          pb={["10px", null, null]}
        >
          <Document
            file={`data:application/pdf;base64,${base64Report}`}
            onLoadSuccess={onDocumentLoadSuccess}
          >
            {Array.from(new Array(numPages), (el, index) => (
              <Page
                scale={isMobile ? 0.77 : 1}
                width={isMobile ? "480" : "1000"}
                key={`page_${index + 1}`}
                pageNumber={index + 1}
              />
            ))}
          </Document>
        </Flex>
      )}
    </MainLayout>
  );
};

export { OrderReportPage };
